// Debug Stream System for Development/Testing

import EXAMPLE_STREAM from './exampleStream2.json';
import { StreamCallbacks } from '../helpers/Types.js';
import { defaultConfig } from '../../../../../utils/config';
import { streamLog } from '../helpers/streamLogger';

export class DebugStream {
  // Uses captured JSON data from exampleStream only
  
  private timeoutIds: NodeJS.Timeout[] = [];
  private callbacks: StreamCallbacks = {};
  private isRunning = false;

  simulate(callbacks: StreamCallbacks) {
    if (this.isRunning) {
      this.stop();
    }
    
    this.callbacks = callbacks;
    this.isRunning = true;
    
    streamLog.init('Starting stream simulation');
    
    // Parse the example stream data into events (with timestamps)
    const events = this.parseJsonStreamEvents(EXAMPLE_STREAM);
    
    streamLog.debug('Parsed', events.length, 'events from stream');
    
    const useRealTiming = !!defaultConfig.DEBUG_STREAM_REAL_TIMING;
    if (useRealTiming) {
      // Schedule based on recorded timestamps when available
      let baseTs = Number.isFinite(events[0]?.ts) ? (events[0]!.ts as number) : 0;
      let lastDelay = 0;
      const scheduled = events
        .slice()
        .sort((a, b) => {
          const ta = Number.isFinite(a.ts) ? (a.ts as number) : Number.MAX_SAFE_INTEGER;
          const tb = Number.isFinite(b.ts) ? (b.ts as number) : Number.MAX_SAFE_INTEGER;
          if (ta !== tb) return ta - tb;
          return 0;
        });
      scheduled.forEach((event) => {
        let delay: number;
        if (Number.isFinite(event.ts)) {
          // Relative to first timestamp
          delay = Math.max(0, (event.ts as number) - baseTs);
          // Ensure non-decreasing schedule to maintain order
          if (delay < lastDelay) {
            delay = lastDelay + 10; // clamp forward minimally
          }
        } else {
          // Fallback to heuristic per-type delay if no timestamp
          delay = lastDelay + this.getEventDelay(event.type);
        }
        lastDelay = delay;
        const timeoutId = setTimeout(() => {
          if (!this.isRunning) return;
          try {
            this.handleEvent(event.type, event.data);
          } catch (error) {
            streamLog.error('Stream parse error:', error);
            this.callbacks.onError?.(new Error(`Debug stream parse error: ${error}`));
          }
        }, delay);
        this.timeoutIds.push(timeoutId);
      });
    } else {
      // Ignore timestamps; use fixed per-type delays to simulate pacing
      let delay = 0;
      events.forEach((event) => {
        const timeoutId = setTimeout(() => {
          if (!this.isRunning) return;
          try {
            this.handleEvent(event.type, event.data);
          } catch (error) {
            streamLog.error('Stream parse error:', error);
            this.callbacks.onError?.(new Error(`Debug stream parse error: ${error}`));
          }
        }, delay);
        this.timeoutIds.push(timeoutId);
        delay += this.getEventDelay(event.type);
      });
    }
  }

  // Parse JSON capture data (from StreamDataCapture)
  private parseJsonStreamEvents(captureData: any): Array<{type: string, data: any, ts?: number}> {
    const events: Array<{type: string, data: any, ts?: number}> = [];
    
    if (captureData && captureData.events) {
      for (const event of captureData.events) {
        events.push({
          type: event.eventType,
          data: event.parsedData || {},
          ts: typeof event.timestamp === 'number' ? event.timestamp : undefined,
        });
      }
    }
    
    return events;
  }

  private getEventDelay(eventType: string): number {
    // Fallback delays when no timestamps are available
    switch (eventType) {
      case 'agent_metadata': return 100;
      case 'agent_tool_call': return 300;
      case 'agent_tool_result': return 500;
      case 'agent_ai_content': return 50;
      case 'agent_complete': return 200;
      case 'agent_done': return 100;
      default: return 200;
    }
  }

  private handleEvent(type: string, data: any) {
    if (!this.isRunning) return;

    // Normalize captured payload to match live stream contract
    const root = (data && typeof data === 'object' && 'data' in (data as any)) ? (data as any).data : data;
    const payload: any = (root && typeof root === 'object') ? { ...(root as any) } : root;
    try {
      if (data && typeof data === 'object' && payload && typeof payload === 'object') {
        if ((data as any).phase) payload.phase = (data as any).phase;
        if ((data as any).timestamp) payload._timestamp = (data as any).timestamp;
      }
    } catch (_) {}

    streamLog.debug(`Stream Event: ${type}`, payload);

    switch (type) {
      case 'agent_metadata':
        this.callbacks.onMetadata?.(payload);
        break;
      case 'agent_tool_call':
        // Captured JSON format - use existing IDs from exampleStream
        this.callbacks.onToolCall?.({
          ...(payload || {}),
          id: (payload as any)?.toolId,
          toolId: (payload as any)?.toolId,
          args: (payload && typeof payload === 'object') ? ((payload as any).args || {}) : {},
          meta: (payload && typeof payload === 'object') ? ((payload as any).meta || {}) : {},
        });
        break;
      case 'agent_tool_result':
        // Captured JSON format - use existing IDs from exampleStream
        this.callbacks.onToolResult?.({
          ...(payload || {}),
          id: (payload as any)?.toolId,
          toolId: (payload as any)?.toolId,
          result: (payload && typeof payload === 'object') ? ((payload as any).result || {}) : {},
          meta: (payload && typeof payload === 'object') ? ((payload as any).meta || {}) : {},
        });
        break;
      case 'agent_ai_content':
        this.callbacks.onAIContent?.(payload);
        break;
      case 'agent_complete':
        this.callbacks.onComplete?.(payload);
        break;
      case 'agent_done':
        streamLog.wrap('Stream completed');
        this.callbacks.onDone?.();
        this.stop();
        break;
    }
  }

  stop() {
    if (this.isRunning) {
      streamLog.wrap('Stopping stream');
    }
    this.isRunning = false;
    this.timeoutIds.forEach(id => clearTimeout(id));
    this.timeoutIds = [];
  }

  isActive(): boolean {
    return this.isRunning;
  }
}
